import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, NotebookPen } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, FormSkeleton, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { DEFAULT_TRIAGE_LEVEL, TRIAGE, type ErCaseDetail } from './er-board';

export function ErTriagePage() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/er');

  const detail = useQuery({
    queryKey: ['er-case', caseId],
    queryFn: () => get<ErCaseDetail>(`/er/cases/${caseId}`),
    enabled: Boolean(caseId),
  });

  if (!detail.data) {
    return (
      <FormScreen title="Triage" icon={Activity} backTo={returnTo}>
        {detail.isLoading ? (
          <FormSkeleton />
        ) : (
          <Alert variant="destructive">{describeError(detail.error)}</Alert>
        )}
      </FormScreen>
    );
  }

  return <ErTriageForm key={detail.data.id} erCase={detail.data} returnTo={returnTo} />;
}

function ErTriageForm({ erCase, returnTo }: { erCase: ErCaseDetail; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialLevel = erCase.triageLevel ?? DEFAULT_TRIAGE_LEVEL;
  const [level, setLevel] = useState<string>(initialLevel);
  const [bay, setBay] = useState('');
  const [notes, setNotes] = useState('');

  const triage = useMutation({
    mutationFn: () =>
      post(`/er/cases/${erCase.id}/triage`, {
        triageLevel: level,
        bayLabel: bay || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      toast.success('Triaged');
      void queryClient.invalidateQueries({ queryKey: ['er-board'] });
      void queryClient.invalidateQueries({ queryKey: ['er-case', erCase.id] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error('Could not triage', describeError(error)),
  });

  const dirty = !triage.isSuccess && (level !== initialLevel || bay !== '' || notes !== '');

  return (
    <FormScreen
      title="Triage"
      description={`${erCase.patient?.fullName ?? erCase.unknownPatientLabel} · ${erCase.number}`}
      icon={Activity}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        triage.mutate();
      }}
    >
      <FormSection title={tr('opd', 'triage')} icon={Activity} columns={1}>
        <Field label="Triage level" required>
          <div className="space-y-1.5">
            {TRIAGE.map((t) => (
              <label
                key={t.level}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                  level === t.level ? t.className : 'border-border',
                )}
              >
                <input type="radio" name="triage" value={t.level} checked={level === t.level} onChange={(e) => setLevel(e.target.value)} />
                <span className="font-semibold">{t.rank}</span> {t.label}
              </label>
            ))}
          </div>
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'notes')} icon={NotebookPen}>
        <Field label="Bay">
          <Input value={bay} onChange={(e) => setBay(e.target.value)} placeholder="Resus 1, Bay 4…" />
        </Field>
        <Field label="Triage note" className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormActions submitLabel="Assign" loading={triage.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
